import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import ImageGallery from './ImageGallery';

import showroom1 from '@/assets/showroom-1.webp';
import showroom2 from '@/assets/showroom-2.webp';
import showroom3 from '@/assets/showroom-3.webp';
import showroom4 from '@/assets/showroom-4.webp';
import showroom5 from '@/assets/showroom-5.webp';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.15, ease: 'easeOut' as const },
  }),
};

const galleryImages = [
  showroom4,
  showroom1,
  showroom2,
  showroom3,
  showroom5,
];

const Showroom = () => {
  const { t } = useTranslation();

  const stats = [
    { value: t('showroom.stat1Value'), label: t('showroom.stat1Label') },
    { value: t('showroom.stat2Value'), label: t('showroom.stat2Label') },
    { value: t('showroom.stat3Value'), label: t('showroom.stat3Label') },
  ];

  return (
    <section id="showroom" className="py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Title + description */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20"
        >
          <motion.div variants={fadeUp} custom={0}>
            <span className="text-xs tracking-[0.3em] uppercase text-primary block mb-4 font-medium">
              {t('showroom.label')}
            </span>
            <h2 className="text-4xl md:text-6xl text-foreground leading-tight font-medium">
              {t('showroom.title')}
            </h2>
          </motion.div>
          <motion.div variants={fadeUp} custom={1} className="flex flex-col justify-end">
            <p className="text-muted-foreground font-light leading-relaxed mb-4">
              {t('showroom.description')}
            </p>
            <p className="text-muted-foreground font-light leading-relaxed">
              {t('showroom.description2')}
            </p>
          </motion.div>
        </motion.div>

        {/* Gallery */}
        <ImageGallery
          images={galleryImages}
          alt="Showroom"
          gridClassName="mb-20"
          renderGrid={(imgs, onImageClick) => (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 auto-rows-[250px] md:auto-rows-[300px]">
              {imgs.map((img, i) => {
                const spanClass = i === 0 ? 'col-span-2 row-span-2' : 'col-span-1 row-span-1';
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: i * 0.1 }}
                    className={`${spanClass} overflow-hidden rounded-lg cursor-pointer`}
                    onClick={() => onImageClick(i)}
                  >
                    <img src={img} alt="Showroom" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
                  </motion.div>
                );
              })}
            </div>
          )}
        />

        {/* Stats */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-3 gap-8 max-w-4xl mx-auto border-t border-border pt-12"
        >
          {stats.map((stat, i) => (
            <motion.div key={i} variants={fadeUp} custom={i} className="text-center">
              <div className="text-4xl md:text-5xl text-primary mb-3 font-medium">{stat.value}</div>
              <div className="text-xs tracking-[0.15em] uppercase text-muted-foreground">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Showroom;
